import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { PrezzarioTree } from "../components/PrezzarioTree";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, UploadCloud, Plus, Trash2, BookMarked, Loader2, MapPin, X, ArrowLeft, ArrowRight, ExternalLink, Database } from "lucide-react";
import { api, euro } from "../lib/api";
import { displayPricebookCode } from "../lib/pricebookCodes";

export default function Prezzari({ session }) {
  const canDeleteRegion = Boolean(session?.user?.is_admin);
  const [catalog, setCatalog] = useState([]);
  const [sourceIndex, setSourceIndex] = useState("https://ance.it/prezzari-regionali/");
  const [catalogError, setCatalogError] = useState("");
  const [allRegions, setAllRegions] = useState([]);
  const [region, setRegion] = useState("");
  const [province, setProvince] = useState("");
  const [sel, setSel] = useState({});
  const [tree, setTree] = useState([]);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 100;
  const [loading, setLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const result = await api.prezzariCatalog();
      setCatalog(result.regions || []);
      setSourceIndex(result.source_index || "https://ance.it/prezzari-regionali/");
      setCatalogError("");
    } catch {
      setCatalogError("Impossibile caricare l'elenco dei prezzari regionali.");
    }
  }, []);

  const load = useCallback(async () => {
    if (!region || (region === "Toscana" && !province)) { setItems([]); setTotal(0); return; }
    setLoading(true);
    try {
      const result = await api.prezzariPage({ region, province: province || undefined, q, macro: sel.macro, category: sel.category, subcategory: sel.subcategory, page, page_size: pageSize });
      setItems(result.items || []);
      setTotal(result.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
      toast.error("Errore durante la lettura del prezzario.");
    } finally {
      setLoading(false);
    }
  }, [region, province, q, sel, page]);

  const loadTree = useCallback(() => {
    if (!region || (region === "Toscana" && !province)) { setTree([]); return; }
    api.prezzariTree(region, province || undefined).then(setTree).catch(() => setTree([]));
  }, [region, province]);

  useEffect(() => {
    loadCatalog();
    api.regions().then(setAllRegions).catch(() => {});
  }, [loadCatalog]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel({}); loadTree(); }, [loadTree]);

  const openRegion = (name) => {
    setQ(""); setSel({}); setPage(0); setRegion(name);
    const entry = catalog.find(x => x.region === name);
    setProvince(name === "Toscana" ? (entry?.provinces?.[0]?.name || "") : "");
  };
  const returnToCatalog = () => { setRegion(""); setProvince(""); setQ(""); setPage(0); setSel({}); };
  const refreshAll = () => { loadCatalog(); loadTree(); load(); };
  const del = async (id) => {
    try { await api.deletePrezzario(id); refreshAll(); toast.success("Voce eliminata"); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const askDelete = (name) => { setDeleteTarget(name); setConfirmationText(""); };
  const deleteRegion = async () => {
    if (!deleteTarget || confirmationText.trim() !== deleteTarget) return;
    setDeleting(true);
    try {
      const result = await api.deleteRegionalPrezzario(deleteTarget);
      toast.success(String(result.deleted) + " voci rimosse dal prezzario " + deleteTarget);
      setDeleteTarget("");
      setConfirmationText("");
      refreshAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossibile eliminare il prezzario");
    } finally { setDeleting(false); }
  };

  const current = catalog.find(x => x.region === region);
  useEffect(() => {
    if (region !== "Toscana") return;
    const provinceNames = current?.provinces?.map(p => p.name) || [];
    if (!province && provinceNames.length) setProvince(provinceNames[0]);
  }, [region, province, current]);
  const regionsForUpload = [...new Set([...catalog.map(x => x.region), ...allRegions])];
  const totalCount = tree.reduce((s, m) => s + m.count, 0);
  const groups = [];
  const gmap = {};
  items.forEach((p) => {
    const key = (p.macro_category || "") + "|" + (p.category || "") + "|" + (p.subcategory || "");
    if (!gmap[key]) {
      gmap[key] = { macro: p.macro_category, category: p.category, subcategory: p.subcategory, rows: [] };
      groups.push(gmap[key]);
    }
    gmap[key].rows.push(p);
  });

  return <div className="space-y-6" data-testid="prezzari-regional-catalog">
    {region ? <>
      <button onClick={returnToCatalog} className="inline-flex gap-2 items-center text-sm font-semibold text-slate-600 hover:text-orange-700" data-testid="prezzari-back-to-catalog">
        <ArrowLeft className="w-4 h-4" /> Tutti i prezzari regionali
      </button>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-widest text-xs font-semibold text-orange-600">Prezzario regionale</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-slate-900 mt-1">{region}</h1>
          {current && <p className="text-sm text-slate-500 mt-2">Fonte ANCE: edizione {current.year}. In archivio ELLEBIERRE: {current.imported_count.toLocaleString("it-IT")} voci{current.imported_year ? " (ultima importazione " + current.imported_year + ")" : ""}.</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={current?.source_url || sourceIndex} target="_blank" rel="noopener noreferrer" data-testid="prezzari-official-link" className="inline-flex gap-2 items-center px-4 py-2.5 border rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50"><ExternalLink className="w-4 h-4" /> Apri prezzario ufficiale</a>
          {region !== "Lazio" && <Link to={`/nuova-offerta?region=${encodeURIComponent(region)}`} data-testid="prezzario-create-offer-button" className="inline-flex gap-2 items-center border border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg text-sm font-semibold px-4 py-2.5"><Plus className="w-4 h-4" /> Crea offerta</Link>}
          <button onClick={() => setShowImport(true)} data-testid="prezzario-import-button" className="inline-flex gap-2 items-center bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold px-4 py-2.5"><UploadCloud className="w-4 h-4" /> Importa file</button>
          <button onClick={() => setShowAdd(true)} data-testid="prezzario-add-manual-button" className="inline-flex gap-2 items-center border rounded-lg bg-white text-slate-700 px-4 py-2.5 text-sm font-semibold"><Plus className="w-4 h-4" /> Voce manuale</button>
          {canDeleteRegion && current?.imported_count > 0 && <button onClick={() => askDelete(region)} data-testid="prezzario-delete-region-button" className="inline-flex gap-2 items-center border border-red-200 rounded-lg text-red-700 px-3 py-2.5 bg-white text-sm font-semibold hover:bg-red-50"><Trash2 className="w-4 h-4" /> Elimina questo prezzario</button>}
        </div>
      </div>
      {region === "Toscana" && current?.provinces?.length > 0 && <p className="text-xs text-slate-500">Dati: <a href="https://dati.toscana.it/dataset/prezzario-lavori-2026" target="_blank" rel="noopener noreferrer" className="text-orange-700 underline">Regione Toscana, Open Data Prezzario 2026</a> · Licenza CC BY 4.0 · Prezzi differenziati per provincia. Le analisi dei prezzi costituiscono un archivio separato non ancora importato.</p>}
      {region === "Toscana" && current?.provinces?.length > 0 && <div className="p-4 rounded-xl bg-white border border-slate-200">
        <label className="block text-sm font-semibold text-slate-800 mb-1">Provincia di riferimento</label>
        <select value={province} onChange={e => { setProvince(e.target.value); setPage(0); setSel({}); setQ(""); }} data-testid="prezzario-province-filter" className="w-full sm:w-72 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          {current.provinces.map(p => <option key={p.name} value={p.name}>{p.name} · {p.count.toLocaleString("it-IT")} voci</option>)}
        </select>
        <p className="text-xs text-slate-500 mt-2">I prezzi della Toscana variano per provincia. Il filtro evita di mescolare listini territoriali diversi.</p>
      </div>}
      {current && !current.imported_count && <div role="status" className="rounded-2xl border border-blue-200 bg-blue-50 p-5 flex gap-3 items-start">
        <Database className="w-5 h-5 mt-0.5 shrink-0 text-blue-700"/>
        <div><h2 className="font-bold text-slate-900">Fonte disponibile, voci non ancora importate</h2>
          <p className="text-sm text-slate-600 mt-1">Puoi consultare il prezzario ufficiale cliccando il collegamento sopra. Per cercarne le voci e utilizzarne i prezzi nei computi ELLEBIERRE occorre importare un file Excel, CSV o PDF compatibile.</p>
        </div>
      </div>}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Cerca per codice o descrizione…" data-testid="prezzario-search-input" className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40"/>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-start">
        <aside className="bg-white border border-slate-200 rounded-xl p-2 lg:sticky lg:top-4 max-h-[75vh] overflow-y-auto">
          <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex justify-between">
            <span>Classificazione della fonte</span><span className="font-mono" data-testid="prezzario-total-count">{totalCount}</span>
          </div>
          <PrezzarioTree tree={tree} selection={sel} onSelect={value => { setSel(value); setPage(0); }} />
        </aside>

        <div className="min-w-0 space-y-2">
          {(sel.macro || q) && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500" data-testid="prezzario-breadcrumb">
              {sel.macro && <span className="px-2 py-1 rounded bg-slate-100 font-semibold text-slate-700">{sel.macro}</span>}
              {sel.category && <><span>›</span><span className="px-2 py-1 rounded bg-slate-100">{sel.category}</span></>}
              {sel.subcategory && <><span>›</span><span className="px-2 py-1 rounded bg-orange-50 text-orange-700">{sel.subcategory}</span></>}
              <button onClick={() => { setSel({}); setQ(""); }} data-testid="prezzario-reset-filters" className="ml-1 inline-flex items-center gap-1 text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /> azzera</button>
              {!loading && <span className="ml-auto" data-testid="prezzario-result-count">{total.toLocaleString("it-IT")} voci trovate</span>}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div>
              : items.length === 0 ? <p className="p-12 text-center text-sm text-slate-400">Nessuna voce di prezzario. Importane uno o aggiungi voci manuali.</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-3 font-semibold w-28">Codice</th>
                        <th className="px-3 py-3 font-semibold">Descrizione</th>
                        <th className="px-3 py-3 font-semibold text-center w-16">U.M.</th>
                        <th className="px-3 py-3 font-semibold text-right w-28">Prezzo</th>
                        <th className="px-2 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {groups.map((g) => (
                        <Fragment key={`${g.macro}|${g.category}|${g.subcategory}`}>
                          <tr className="bg-slate-50/80">
                            <td colSpan={5} className="px-3 py-2 text-[11px] text-slate-500">
                              <span className="inline-flex items-center gap-1 text-slate-400"><MapPin className="w-3 h-3" />{g.rows[0].region}</span>
                              <span className="mx-2 text-slate-300">|</span>
                              <span className="font-semibold text-slate-600 uppercase">{g.macro}</span>
                              {g.category && <><span className="mx-1.5 text-slate-300">›</span><span className="font-display font-semibold text-slate-800">{g.category}</span></>}
                              {g.subcategory && <><span className="mx-1.5 text-slate-300">›</span><span className="text-orange-700 font-semibold">{g.subcategory}</span></>}
                            </td>
                          </tr>
                          {g.rows.map((p) => (
                            <tr key={p.id} data-testid={`prezzario-row-${p.id}`} className="hover:bg-slate-50 align-top">
                              <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">
                                {p.code ? <span className="text-orange-600" data-testid={`prezzario-code-${p.id}`}>{displayPricebookCode(p)}</span>
                                  : <span className="text-slate-400" title="Voce senza codice proprio: sotto-voce di">↳ {p.parent_code || "—"}</span>}
                              </td>
                              <td className="px-3 py-2.5 text-slate-800">
                                {p.description !== p.short_description && p.short_description ? (
                                  <>
                                    <span className="text-slate-500">{p.description.slice(0, p.description.length - p.short_description.length)}</span>
                                    <span className="font-medium">{p.short_description}</span>
                                  </>
                                ) : p.description}
                              </td>
                              <td className="px-3 py-2.5 text-center text-slate-600">{p.unit}</td>
                              <td className="px-3 py-2.5 text-right font-mono font-semibold whitespace-nowrap">{p.region === "Toscana" && p.year === "2026" ? new Intl.NumberFormat("it-IT", {style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 5}).format(p.price) : euro(p.price)}</td>
                              <td className="px-2 py-2.5 text-center"><button onClick={() => del(p.id)} data-testid={`prezzario-delete-${p.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>
      {total > pageSize && <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid="prezzario-pagination">
        <span className="text-xs sm:text-sm text-slate-600">Voci {(page * pageSize + 1).toLocaleString("it-IT")}–{Math.min(total, (page + 1) * pageSize).toLocaleString("it-IT")} di {total.toLocaleString("it-IT")}</span>
        <div className="flex gap-2">
          <button type="button" disabled={page === 0 || loading} onClick={() => setPage(v => Math.max(0, v - 1))} className="text-sm px-3 py-2 border rounded-lg disabled:opacity-40">Precedenti</button>
          <button type="button" disabled={loading || (page + 1) * pageSize >= total} onClick={() => setPage(v => v + 1)} className="text-sm px-3 py-2 border rounded-lg disabled:opacity-40" data-testid="prezzario-page-next">Successivi</button>
        </div>
      </div>}

    </> : <>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-orange-600 uppercase tracking-widest text-xs font-bold">Ellebierre · Archivio prezzi</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-slate-900 mt-2">Prezzari regionali</h1>
          <p className="text-slate-500 mt-2 text-sm">Tutte le regioni italiane e le due province autonome. Apri una scheda per consultare il prezzario e gestire le sue voci.</p>
        </div>
        <a href={sourceIndex} target="_blank" rel="noopener noreferrer" className="inline-flex gap-2 items-center border rounded-lg px-4 py-2.5 bg-white text-slate-700 text-sm font-semibold hover:border-orange-300"><ExternalLink className="w-4 h-4" /> Elenco ANCE</a>
      </div>
      {catalogError && <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm">{catalogError} Riprova aggiornando la pagina.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="prezzari-region-cards">
        {catalog.map((entry, i) => <article key={entry.region} className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:border-orange-300 hover:shadow-md transition-shadow">
          <button type="button" onClick={() => openRegion(entry.region)} data-testid={"prezzario-card-" + i} className="w-full h-full text-left p-5 pb-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-600">
            <div className="flex justify-between items-start gap-2">
              <div className="rounded-xl bg-orange-50 text-orange-600 p-3"><MapPin className="w-5 h-5"/></div>
              <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Ed. {entry.year}</span>
            </div>
            <h2 className="mt-4 text-lg font-bold text-slate-900 leading-tight">{entry.region}</h2>
            <p className="mt-2 text-sm text-slate-500">{entry.imported_count > 0 ? entry.imported_count.toLocaleString("it-IT") + " voci importate" : "Fonte ufficiale · Da importare"}</p>
            <span className="inline-flex mt-5 gap-2 items-center text-orange-700 text-sm font-semibold">Apri prezzario <ArrowRight className="w-4 h-4" /></span>
          </button>
          {canDeleteRegion && entry.imported_count > 0 && <button type="button" onClick={() => askDelete(entry.region)} title={"Elimina il prezzario di " + entry.region} aria-label={"Elimina prezzario di " + entry.region} data-testid={"prezzario-delete-card-" + i} className="absolute bottom-4 right-4 p-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>}
        </article>)}
      </div>
      {!catalogError && catalog.length === 0 && <p className="text-sm text-slate-500">Caricamento catalogo…</p>}
      <p className="text-xs text-slate-500">Anni e collegamenti: elenco ANCE verificato il 3 novembre 2026. Le fonti ufficiali sono collegate, ma le voci non vengono considerate importate finché non risultano nel database.</p>
    </>}
    {showImport && <ImportModal regions={regionsForUpload} initialRegion={region} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); refreshAll(); }} />}
    {showAdd && <AddModal regions={regionsForUpload} initialRegion={region} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); refreshAll(); }} />}
    {deleteTarget && <Modal title={"Elimina prezzario · " + deleteTarget} onClose={() => !deleting && setDeleteTarget("")}>
      <p className="text-sm text-slate-600">Verranno rimosse soltanto le voci importate del prezzario <strong>{deleteTarget}</strong>. Le altre regioni non saranno interessate. La scheda e la fonte ufficiale resteranno disponibili per una futura importazione.</p>
      <label className="block text-xs font-semibold text-slate-600 mt-4 mb-1">Digita il nome esatto della regione per confermare</label>
      <input data-testid="prezzario-confirm-delete-region" value={confirmationText} onChange={e => setConfirmationText(e.target.value)} placeholder={deleteTarget} className="w-full border rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={() => setDeleteTarget("")} disabled={deleting} className="rounded-lg border px-4 py-2 text-sm">Annulla</button>
        <button onClick={deleteRegion} disabled={deleting || confirmationText.trim() !== deleteTarget} data-testid="prezzario-confirm-delete" className="bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-lg text-white font-semibold text-sm px-4 py-2">Elimina solo questo prezzario</button>
      </div>
    </Modal>}
  </div>;
}

function ImportModal({ regions, initialRegion, onClose, onDone }) {
  const [region, setRegion] = useState(initialRegion || regions[0] || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef();

  const submit = async () => {
    if (!region || !file) { toast.error("Seleziona regione e file"); return; }
    setBusy(true);
    try {
      if (region === "Lazio") {
        const result = await api.previewPrezzario(region, file);
        setPreview(result);
        toast.success("Anteprima completata senza modificare i prezzari");
      } else {
        const r = await api.importPrezzario(region, file);
        toast.success(`${r.imported} voci per ${region}: ${r.inserted} nuove, ${r.updated} aggiornate (${r.with_price} con prezzo)`);
        onDone();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'importazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Importa prezzario regionale" onClose={onClose}>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">Regione</label>
      <select value={region} onChange={(e) => { setRegion(e.target.value); setFile(null); setPreview(null); }} data-testid="import-region-select" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-orange-500/40">
        {regions.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      {region === "Lazio" && <div role="status" className="border border-amber-200 bg-amber-50 rounded-xl p-3 mb-3 text-sm text-amber-900">
        <strong>Lazio 2023 · verifica sicura.</strong> Carica lo ZIP ufficiale con le nove sezioni oppure un CSV. Il file sarà analizzato <strong>senza salvare dati</strong>: l’importazione definitiva è protetta finché il parser regionale non sarà validato.
      </div>}
      <div onClick={() => inputRef.current?.click()} data-testid="import-file-dropzone" className="cursor-pointer border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400">
        <input ref={inputRef} type="file" hidden accept={region === "Lazio" ? ".zip,.csv" : ".xlsx,.xls,.pdf,.csv"} onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} data-testid="import-file-input" />
        <UploadCloud className="w-7 h-7 text-slate-400 mx-auto" />
        <p className="text-sm text-slate-600 mt-2">{file ? file.name : region === "Lazio" ? "Scegli ZIP ufficiale Lazio 2023 o singolo CSV" : "Clicca per selezionare (Excel/PDF/CSV)"}</p>
      </div>
      {region !== "Lazio" && <p className="text-xs text-slate-400 mt-2">Formati: Excel/CSV con colonne Codice, Descrizione, U.M., Prezzo. Il parser generico è da validare sul formato regionale prima di utilizzare i prezzi nei computi.</p>}
      {preview && <div data-testid="lazio-preview-result" className="mt-4 rounded-xl border border-slate-200 p-3 bg-slate-50 space-y-2 max-h-64 overflow-auto">
        <h3 className="font-bold text-sm text-slate-900">Esito verifica · nessun dato importato</h3>
        <p className="text-sm text-slate-700">{preview.sections?.length} file analizzati · {preview.recognized_rows?.toLocaleString("it-IT")} righe riconosciute dal parser generico · {preview.missing_code_rows} senza codice · {preview.cross_section_duplicate_codes} codici presenti in più sezioni.</p>
        {(preview.sections || []).map(x => <p className="text-xs text-slate-700" key={x.filename}>{x.section}: {x.recognized_rows?.toLocaleString("it-IT")} righe · {x.filename}</p>)}
        <p className="text-xs font-semibold text-amber-800">Questi conteggi non attestano l’accuratezza delle voci. L’importazione è disabilitata finché il parser Lazio non avrà superato i controlli.</p>
      </div>}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600">Annulla</button>
        <button onClick={submit} disabled={busy} data-testid="import-submit-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {region === "Lazio" ? "Verifica senza importare" : "Importa"}
        </button>
      </div>
    </Modal>
  );
}

function AddModal({ regions, initialRegion, onClose, onDone }) {
  const [f, setF] = useState({ region: initialRegion || regions[0] || "", code: "", description: "", unit: "", price: 0, category: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.region || !f.description) { toast.error("Regione e descrizione obbligatorie"); return; }
    await api.addPrezzario(f);
    toast.success("Voce aggiunta");
    onDone();
  };
  const Field = ({ label, k, type = "text", full }) => (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <input type={type} value={f[k]} onChange={(e) => set(k, type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)} data-testid={`add-prezzario-${k}`}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
    </div>
  );
  return (
    <Modal title="Nuova voce di prezzario" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Regione</label>
          <select value={f.region} onChange={(e) => set("region", e.target.value)} data-testid="add-prezzario-region" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40">
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Field label="Codice" k="code" />
        <Field label="Descrizione" k="description" full />
        <Field label="Categoria" k="category" />
        <Field label="U.M." k="unit" />
        <Field label="Prezzo (€)" k="price" type="number" />
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600">Annulla</button>
        <button onClick={submit} data-testid="add-prezzario-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold"><Plus className="w-4 h-4" /> Aggiungi</button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 sm:p-10" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6 fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2"><BookMarked className="w-5 h-5 text-orange-600" /> {title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
